import moment from 'moment';
import { getTranslation } from '../utils/translation';
import LocalStorage, { LOCAL_STORAGE_KEYS } from '../utils/localStorage';
import { store } from '../store';
import { changeEventFilters } from '../store/actions/sportEvents';
import { sortEventsByLeague } from '../utils/event';

export const EVENT_FILTER_OPTIONS = {
    categories: {
        all: {
            id: 'all',
            label: getTranslation('eventsFiltersCategoriesAll'),
        },
    },
    leagues: {
        all: {
            id: 'all',
            label: getTranslation('eventsFiltersLeaguesAll'),
        },
        favorites: {
            id: 'favorites',
            label: getTranslation('eventsFiltersLeaguesFavorites'),
        },
    },
    periods: {
        all: { id: 'all', label: getTranslation('eventsFiltersPeriodsAll') },
        firstPeriod: { id: '1', label: getTranslation('eventsFiltersPeriodsFirstItem') },
        fifthPeriod: { id: '5', label: getTranslation('eventsFiltersPeriodsFifthItem') },
        secondPeriod: { id: '2', label: getTranslation('eventsFiltersPeriodsSecondItem') },
        thirdPeriod: { id: '3', label: getTranslation('eventsFiltersPeriodsThirdItem') },
        fourthPeriod: { id: '4', label: getTranslation('eventsFiltersPeriodsFourthItem') },
    },
    orders: {
        byLeague: {
            id: '1',
            label: getTranslation('eventsFiltersOrdersFirstItem'),
        },
        byTime: {
            id: '2',
            label: getTranslation('eventsFiltersOrdersSecondItem'),
        },
        byMbn: {
            id: '3',
            label: getTranslation('eventsFiltersOrdersThirdItem'),
        },
    },
};

export const INITIAL_EVENT_FILTER_OPTIONS = {
    categories: [
        {
            ...EVENT_FILTER_OPTIONS.categories.all,
            selected: true,
        },
    ],
    leagues: [
        {
            ...EVENT_FILTER_OPTIONS.leagues.all,
            selected: true,
        },
        {
            ...EVENT_FILTER_OPTIONS.leagues.favorites,
            selected: false,
        },
    ],
    eSportLeagues: [
        {
            ...EVENT_FILTER_OPTIONS.leagues.all,
            selected: true,
        },
    ],
    periods: [
        {
            ...EVENT_FILTER_OPTIONS.periods.all,
            selected: false,
            getRange: () => getRange(null, null),
        },
        {
            ...EVENT_FILTER_OPTIONS.periods.firstPeriod,
            selected: false,
            getRange: () => getRange(moment(), moment().add(6, 'hours')),
        },
        {
            ...EVENT_FILTER_OPTIONS.periods.fifthPeriod,
            selected: false,
            getRange: () => getRange(moment(), moment().add(24, 'hours')),
        },
        {
            ...EVENT_FILTER_OPTIONS.periods.secondPeriod,
            selected: true,
            getRange: () => getRange(moment().startOf('day'), moment().endOf('day')),
        },
        {
            ...EVENT_FILTER_OPTIONS.periods.thirdPeriod,
            selected: false,
            getRange: () =>
                getRange(
                    moment().add(1, 'days').startOf('day'),
                    moment().add(1, 'days').endOf('day'),
                ),
        },
        {
            ...EVENT_FILTER_OPTIONS.periods.fourthPeriod,
            selected: false,
            getRange: () => getRange(moment().startOf('isoWeek'), moment().endOf('isoWeek')),
        },
    ],
    orders: [
        {
            ...EVENT_FILTER_OPTIONS.orders.byLeague,
            selected: true,
        },
        {
            ...EVENT_FILTER_OPTIONS.orders.byTime,
            selected: false,
        },
        {
            ...EVENT_FILTER_OPTIONS.orders.byMbn,
            selected: false,
        },
    ],
};

export const initialOutrightFilters = {
    categories: INITIAL_EVENT_FILTER_OPTIONS.categories,
    leagues: INITIAL_EVENT_FILTER_OPTIONS.leagues,
    orders: INITIAL_EVENT_FILTER_OPTIONS.orders,
};

export const initialPreMatchFilters = {
    categories: INITIAL_EVENT_FILTER_OPTIONS.categories,
    leagues: INITIAL_EVENT_FILTER_OPTIONS.leagues,
    periods: INITIAL_EVENT_FILTER_OPTIONS.periods,
    orders: INITIAL_EVENT_FILTER_OPTIONS.orders,
};

export const initialESportPreMatchFilters = {
    categories: INITIAL_EVENT_FILTER_OPTIONS.categories,
    leagues: INITIAL_EVENT_FILTER_OPTIONS.eSportLeagues,
    periods: INITIAL_EVENT_FILTER_OPTIONS.periods,
    orders: INITIAL_EVENT_FILTER_OPTIONS.orders,
};

export const initialLiveFilters = {
    leagues: INITIAL_EVENT_FILTER_OPTIONS.leagues,
    orders: INITIAL_EVENT_FILTER_OPTIONS.orders,
};

export const initialESportLiveFilters = {
    leagues: INITIAL_EVENT_FILTER_OPTIONS.eSportLeagues,
    orders: INITIAL_EVENT_FILTER_OPTIONS.orders,
};

const getRange = (start, end) => ({
    startTime: start ? start.valueOf() : start,
    endTime: end ? end.valueOf() : end,
});

const defaultFilters = {
    category: INITIAL_EVENT_FILTER_OPTIONS.categories.find(c => c.selected).id,
    league: INITIAL_EVENT_FILTER_OPTIONS.leagues.find(c => c.selected).id,
    period: INITIAL_EVENT_FILTER_OPTIONS.periods.find(c => c.selected).id,
    order: INITIAL_EVENT_FILTER_OPTIONS.orders.find(c => c.selected).id,
};

export const getSavedEventFilters = ({ sportRefId, isLive, isOutrights }) => {
    let savedFilters = LocalStorage.getItem(LOCAL_STORAGE_KEYS.eventFilters);
    savedFilters = isLive
        ? savedFilters?.live
        : isOutrights
          ? savedFilters?.outright
          : savedFilters?.preMatch;
    savedFilters = savedFilters?.[sportRefId] || defaultFilters;
    return savedFilters;
};

export const saveEventFilters = ({ filters, sportRefId, isLive, isOutrights }) => {
    const competitionType = isLive ? 'live' : isOutrights ? 'outright' : 'preMatch';

    const savedFilters = LocalStorage.getItem(LOCAL_STORAGE_KEYS.eventFilters) || {
        preMatch: {},
        live: {},
        outright: {},
    };

    const newFilters = {};

    if (filters.categories) {
        newFilters.category = filters.categories.find(v => v.selected).id;
    }

    newFilters.league = filters.leagues.find(v => v.selected).id;
    newFilters.order = filters.orders.find(v => v.selected).id;

    if (filters.periods) {
        newFilters.period = filters.periods.find(v => v.selected).id;
    }

    savedFilters[competitionType] = {
        ...savedFilters[competitionType],
        [sportRefId]: newFilters,
    };

    LocalStorage.setItem(LOCAL_STORAGE_KEYS.eventFilters, savedFilters);
};

export const getInitialEventFilters = (isLive, isESports, isOutrights) => {
    if (isLive) {
        if (isESports) return initialESportLiveFilters;
        return initialLiveFilters;
    } else {
        if (isOutrights) return initialOutrightFilters;
        if (isESports) return initialESportPreMatchFilters;
        return initialPreMatchFilters;
    }
};

export const generateEventFilters = ({ sportRefId, isLive, isESports, isOutrights }) => {
    const savedFilters = getSavedEventFilters({ sportRefId, isLive, isOutrights });
    const initialFilters = getInitialEventFilters(isLive, isESports, isOutrights);

    if (initialFilters.periods) {
        initialFilters.periods = initialFilters.periods.map(period => ({
            ...period,
            selected: period.id === savedFilters.period,
        }));
    }

    initialFilters.orders = initialFilters.orders.map(order => ({
        ...order,
        selected: order.id === savedFilters.order,
    }));

    return initialFilters;
};

export const mergeEventFilters = (currentFilters, newFilters, savedFilters) => {
    const mergedFilters = { ...currentFilters };

    if (mergedFilters.categories) {
        const initialCategories = INITIAL_EVENT_FILTER_OPTIONS.categories;
        const isSavedCategoryFound = newFilters.categories.find(
            c => c.id === savedFilters.category,
        );

        mergedFilters.categories = currentFilters.categories
            .concat(newFilters.categories)
            .map(category => ({
                ...category,
                selected: isSavedCategoryFound
                    ? category.id === savedFilters.category
                    : category.id === initialCategories.find(c => c.selected).id,
            }));
    }

    if (mergedFilters.leagues) {
        const initialLeagues = INITIAL_EVENT_FILTER_OPTIONS.leagues;
        const foundSavedLeague = newFilters.leagues.find(l => l.id === savedFilters.league);

        mergedFilters.leagues = currentFilters.leagues.concat(newFilters.leagues).map(league => ({
            ...league,
            selected: foundSavedLeague
                ? league.id === savedFilters.league
                : league.id === initialLeagues.find(l => l.selected).id,
        }));

        if (foundSavedLeague && mergedFilters.categories) {
            mergedFilters.categories = mergedFilters.categories.map(category => ({
                ...category,
                selected: category.id === foundSavedLeague.categoryId,
            }));
        }
    }

    if (mergedFilters.periods) {
        mergedFilters.periods = mergedFilters.periods.map(period => ({
            ...period,
            selected: period.id === savedFilters.period,
        }));
    }

    if (mergedFilters.orders) {
        mergedFilters.orders = mergedFilters.orders.map(order => ({
            ...order,
            selected: order.id === savedFilters.order,
        }));
    }

    return mergedFilters;
};

export const updateEventCount = (items, id, diff) => {
    let selectedItemEventCount;

    const updatedItems = items
        .map(item => {
            if (item.id === id) {
                const updatedEventCount = item.eventCount + diff;

                if (item.selected) {
                    selectedItemEventCount = updatedEventCount;
                }

                return { ...item, eventCount: updatedEventCount };
            }

            return item;
        })
        .filter(c => c.eventCount === undefined || c.eventCount > 0);

    if (selectedItemEventCount === 0) {
        items[0].selected = true;
    }

    return updatedItems;
};

export const sortFilters = (items, locale) => {
    items.sort(sortEventsByLeague(locale));
};

export const MODIFY_FILTER_TYPES = {
    add: 'add',
    delete: 'delete',
};

const modifyFilter = ({ items, payload, locale, actionType, count = 1 }) => {
    let newItems = [...items];

    const foundItem = newItems.find(item => item.id === payload.id);

    if (foundItem) {
        newItems = updateEventCount(
            newItems,
            payload.id,
            actionType === MODIFY_FILTER_TYPES.add ? count : -count,
        );
    } else {
        if (actionType === MODIFY_FILTER_TYPES.add) {
            newItems.push({ ...payload, eventCount: 1 });
            sortFilters(newItems, locale);
        }
    }

    return newItems;
};

export const modifyFilters = ({ filters, payload, locale, actionType, count }) => {
    const { dispatch } = store;

    const newFilters = Object.entries(filters).reduce(
        (res, [key, value]) => ({ ...res, [key]: [...value] }),
        {},
    );

    let shouldFetchEvents = false;
    let shouldModifyEvents;

    if (filters.categories) {
        const { category } = payload;
        const currentSelectedCategory = newFilters.categories.find(c => c.selected);
        newFilters.categories = modifyFilter({
            items: newFilters.categories,
            payload: category,
            locale,
            actionType,
            count,
        });

        const newSelectedCategory = newFilters.categories.find(c => c.selected);
        const isSelectedCategoryChanged = currentSelectedCategory.id !== newSelectedCategory.id;

        const isAllCategoriesSelected = newFilters.categories.find(
            c => c.id === EVENT_FILTER_OPTIONS.categories.all.id,
        ).selected;
        shouldFetchEvents = shouldFetchEvents || isSelectedCategoryChanged;
        shouldModifyEvents =
            shouldModifyEvents || newSelectedCategory.id === category.id || isAllCategoriesSelected;
    }

    if (filters.leagues) {
        const { league } = payload;

        const currentSelectedLeague = newFilters.leagues.find(l => l.selected);

        newFilters.leagues = modifyFilter({
            items: newFilters.leagues,
            payload: league,
            locale,
            actionType,
            count,
        });

        const newSelectedLeague = newFilters.leagues.find(l => l.selected);
        const isSelectedLeagueChanged = currentSelectedLeague.id !== newSelectedLeague.id;
        const isAllLeaguesSelected = newFilters.leagues.find(
            l => l.id === EVENT_FILTER_OPTIONS.leagues.all.id,
        ).selected;

        shouldFetchEvents = shouldFetchEvents || isSelectedLeagueChanged;
        shouldModifyEvents =
            shouldModifyEvents === undefined
                ? isAllLeaguesSelected
                : shouldModifyEvents && isAllLeaguesSelected;
    }

    dispatch(changeEventFilters({ filters: newFilters, shouldUpdateEvents: shouldFetchEvents }));

    return {
        filters: newFilters,
        shouldModifyEvents: shouldFetchEvents ? false : shouldModifyEvents,
    };
};
